import {
    getItineraryEvents as getItineraryEventsApi,
    getProviderItineraryEvents as getProviderItineraryEventsApi,
    addItineraryEvent as addItineraryEventApi,
    deleteItineraryEvent as deleteItineraryEventApi,
} from './api';

/**
 * Fetch itinerary events from the database
 * @param {string} userId - The user ID to fetch events for
 * @param {Date} start - Start of the range
 * @param {Date} end - End of the range
 * @returns {Promise<Array>} Array of event objects
 */
export const fetchItineraryEvents = async (userId, start, end) => {
    if (!userId) return [];
    try {
        const events = await getItineraryEventsApi(userId, start, end);
        return events;
    } catch (error) {
        console.error('Error fetching itinerary events:', error);
        return [];
    }
};

/**
 * Fetch provider itinerary events (listings favorited by customers)
 * @param {string} userId - The provider's user ID
 * @param {Date} start - Start of the range
 * @param {Date} end - End of the range
 * @returns {Promise<Array>} Array of event objects
 */
export const fetchProviderItineraryEvents = async (userId, start, end) => {
    if (!userId) return [];
    try {
        const events = await getProviderItineraryEventsApi(userId, start, end);
        return events;
    } catch (error) {
        console.error('Error fetching provider itinerary events:', error);
        return [];
    }
};

/**
 * Save a new itinerary event to the database
 * @param {Object} eventData - The event data to save
 * @returns {Promise<Object>} Result object with success status and optional error message
 */
export const addItineraryEvent = async (eventData) => {
    try {
        const result = await addItineraryEventApi(eventData);
        return { success: true, data: result };
    } catch (error) {
        console.error('Error adding itinerary event:', error);
        
        // The error object contains the response data directly
        // Check if it's a duplicate error (409 Conflict)
        if (error.status === 409 || error.error === 'Event already exists') {
            return { 
                success: false, 
                isDuplicate: true,
                message: error.message || 'This event is already on your calendar.'
            };
        }
        
        // Other errors
        return { 
            success: false, 
            isDuplicate: false,
            message: error.message || error.error || 'Failed to add event to your calendar. Please try again.'
        };
    }
};

/**
 * Remove a specific itinerary event from the database
 * @param {string} eventId - The ID of the event to remove
 * @returns {Promise<boolean>} Success status
 */
export const deleteItineraryEvent = async (eventId) => {
    try {
        await deleteItineraryEventApi(eventId);
        return true;
    } catch (error) {
        console.error('Error deleting itinerary event:', error);
        return false;
    }
};

/**
 * Convert DB events to calendar events format
 * @param {Array} events - Array of DB event objects
 * @returns {Array} Array of calendar event objects
 */
export const dbEventsToCalendarEvents = (events) => {
    if (!Array.isArray(events)) return [];

    /* One card per booking, and one card for a session with no booking on it.
       flatMap rather than map because a guest who booked the same session twice has two
       of them: see cardsFor. */
    return events.flatMap(cardsFor);
};

/**
 * The cards one saved session comes to.
 *
 * A session the guest only saved, or one whose bookings have all ended, is one card — the
 * saved entry itself, which is what it has always been. A session they booked is one card
 * per booking.
 *
 * That is not a presentation choice. Two bookings have two sets of seats, two answers from
 * the provider and two message threads, and a single card can hold one of each: it read
 * "Confirmed · 5 guests" across a booking the provider had accepted and another they had
 * not yet looked at, and a message from it went to whichever thread the query returned
 * first.
 *
 * Each card keeps the saved entry's id alongside its own, because removing a session or
 * moving it to another date acts on the entry, not on a booking — see eventIdOf.
 */
const cardsFor = event => {
    const bookings = Array.isArray(event?.bookings) ? event.bookings : [];
    const ended = Array.isArray(event?.endedRequests) ? event.endedRequests : [];
    if (bookings.length === 0 && ended.length === 0) return [oneCard(event)];

    return [
        ...bookings.map(booking => oneCard(event, booking)),
        /* A refusal the guest has since answered by booking the session again. It keeps
           its card — it happened, and the card is where its seats, its date and its thread
           live — but it is not asking for anything, so it is marked and the section that
           collects what still needs an answer leaves it out. */
        ...ended.map(request => oneCard(event, null, { ...request, superseded: bookings.length > 0 })),
    ];
};

/**
 * The saved entry's own id, whichever card is asking.
 *
 * A card drawn for a booking carries a compound id of its own so two of them are told
 * apart, and the entry's id underneath it. Anything that writes to the itinerary — a
 * removal, the delete half of a move — has to use the one underneath.
 */
export const eventIdOf = event => event?.resource?.eventId ?? event?.id ?? null;

const oneCard = (event, booking = null, ended = null) => {
    /* The booking's own start where there is one. Bookings are matched to a saved
       entry by the day they fall on, so two sittings of one variant on one day both
       match it; taking the time from the entry would draw them at the same hour. */
    const which = booking || ended;
    const actualStart = new Date(which?.startAt ?? event.startDate);
    const actualEnd = new Date(which?.endAt ?? event.endDate);

    // For week/day views: show event at start time slot only
    // Make end time 1 hour after start time (same day)
    const displayEnd = new Date(actualStart.getTime() + 60 * 60 * 1000);

    return {
        // One card per booking needs one id per card; the entry's id is kept below.
        id: which?.bookingId ? `${event._id}:${which.bookingId}` : event._id,
        title: event.listingTitle,
        start: actualStart,
        end: displayEnd, // 1 hour after start for proper slot display
        allDay: false,
        resource: {
            // What a removal or a move acts on, which is never the booking.
            eventId: event._id,
            listingId: event.listingId,
            variantName: event.variantName,
            listing: event.listing,
            originalStartDate: actualStart,
            originalEndDate: actualEnd,
            // A card for something that ended holds nothing on the session now, which
            // is what the folded fields said whenever they reported an ending.
            isBooked: ended ? false : booking ? true : event.isBooked ?? false,
            // A booking the provider has accepted, as against one still asked for.
            isConfirmed: ended ? false : booking ? booking.status === 'confirmed' : event.isConfirmed ?? false,
            // What the session itself is like, for the summary a guest sees before
            // booking: how long it runs, how many seats it holds, and how many of those
            // are taken. A count of seats, never who holds them.
            durationMinutes: event.durationMinutes ?? null,
            capacity: event.capacity ?? null,
            /* How many seats the guest said they wanted when they saved the date. Null
               on anything saved before the field existed, which the popup opens at the
               session's fewest rather than guessing a number for them. */
            savedSeats: event.seats ?? null,
            /* Which named option this booking was bought as, where the session carries
               no variant of its own — a Console one. It is what the popup opens its
               option picker on when the guest comes back for more seats, rather than
               asking again for an answer they have already paid for. Null on a saved
               date, on anything booked before the name was kept, and on a booking the
               provider logged by hand. */
            bookedVariantName: which?.priceVariantName ?? null,
            confirmedSeats: event.confirmedSeats ?? 0,
            // And what a seat on it costs. From the session rather than from the saved
            // listing, which prices no variant and names the cheapest — see perSeatPrice
            // in eventSummary, which this is the carriage for.
            seatPrice: event.seatPrice ?? null,
            seatCurrency: event.seatCurrency ?? null,
            // Only a booking records how many people the guest booked for. This
            // booking's own seats, not every booking's added together.
            bookedSeats: which ? which.seatsBooked ?? 1 : event.bookedSeats ?? null,
            // And what this booking came to, as it was recorded when it was made. A
            // session merely saved has none: nothing has been charged for it.
            bookingTotal: which?.totalPrice ?? null,
            // How a booking on this session ended, when the guest holds nothing on it
            // now: not accepted, declined or cancelled. The itinerary draws all three
            // and tells them apart in words, and the aggregation computes it — but it
            // stopped here, so every ended booking reached the dashboard looking like
            // a session that was merely saved.
            // A card drawn for a live booking has not ended, whatever became of the
            // guest's other attempts on the same session.
            endedBy: ended ? ended.endedBy ?? null : booking ? null : event.endedBy ?? null,
            /* Already answered by a booking the guest went on to make, so it is on the
               record rather than on the list of things still owed an answer. */
            superseded: ended?.superseded === true,
            timezone: event.timezone ?? null,
            // The booking's own transaction, which is the thread a message to the
            // host belongs on. Absent on a session the guest has only saved: there
            // is nothing to talk on yet, and writing one opens an inquiry instead.
            txId: which ? which.txId ?? null : event.txId ?? null,
        },
    };
};

/**
 * Convert Provider DB events to calendar events format
 * Server now returns pre-grouped documents with interestedCount and bookedCount.
 * @param {Array} groups - Array of server-grouped event documents
 * @returns {Array} Array of calendar event objects
 */
export const providerEventsToCalendarEvents = (groups) => {
    if (!Array.isArray(groups)) return [];

    return groups.map((group) => {
        const actualStart = new Date(group.startDate);
        const actualEnd = new Date(group.endDate);

        // For week/day views: show event at start time slot only
        // Make end time 1 hour after start time (same day)
        const displayEnd = new Date(actualStart.getTime() + 60 * 60 * 1000);

        return {
            id: `group_${group._id?.listingId ?? group.listingId}_${actualStart.getTime()}_${group._id?.variantName ?? group.variantName ?? 'no-variant'}`,
            title: group.listingTitle,
            start: actualStart,
            end: displayEnd, // 1 hour after start for proper slot display
            allDay: false,
            resource: {
                listingId: group._id?.listingId ?? group.listingId,
                variantName: group._id?.variantName ?? group.variantName,
                listing: group.listing,
                originalStartDate: actualStart,
                originalEndDate: actualEnd,
                interestedCount: group.interestedCount,
                bookedCount: group.bookedCount,
                isProviderView: true,
            },
        };
    });
};